/* eslint-disable prefer-rest-params */ import { useMemo, useRef, useSyncExternalStore } from "react";
import { computed, react } from "../core/index.mjs";
/**
 * Shallow equality check for objects
 */ function shallowEqual(a, b) {
    if (Object.is(a, b)) return true;
    if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) {
        return false;
    }
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (const key of keysA){
        if (!Object.prototype.hasOwnProperty.call(b, key) || !Object.is(a[key], b[key])) {
            return false;
        }
    }
    return true;
}
/**
 * Like useValue, but only triggers re-renders when the computed value actually changes.
 *
 * This is useful when the computation reads from reactive state that changes frequently
 * (like hover state), but the derived value changes rarely.
 *
 * @example
 * ```ts
 * This will only re-render when deviceType or orientation actually changes,
 * not on every store change (like hover events)
 * const { deviceType, orientation } = useStableValue(
 *   "previewState",
 *   () => ({
 *     deviceType: instance.preview?.device || "phone",
 *     orientation: instance.preview?.orientation || "portrait",
 *   }),
 *   [editor]
 * )
 * ```
 *
 * @param name - Debug name for the computation
 * @param fn - Function that computes the value
 * @param deps - Dependencies array (like useMemo)
 * @param isEqual - Optional custom equality function (defaults to shallow equal)
 * @public
 */ export function useStableValue(name, fn, deps, isEqual = shallowEqual) {
    const isInRender = useRef(true);
    isInRender.current = true;
    // Cache for the stable value - only updates when content actually changes
    const cachedValueRef = useRef({
        value: undefined,
        initialized: false
    });
    const $val = useMemo(()=>{
        return computed(name, ()=>{
            if (isInRender.current) {
                return fn();
            } else {
                try {
                    return fn();
                } catch  {
                    // Same zombie-child handling as useValue
                    return {};
                }
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    try {
        const { subscribe, getSnapshot } = useMemo(()=>{
            return {
                subscribe: (listen)=>{
                    return react(`useStableValue(${name})`, ()=>{
                        const newValue = $val.get();
                        // Only notify React if the value actually changed
                        if (!cachedValueRef.current.initialized || !isEqual(cachedValueRef.current.value, newValue)) {
                            cachedValueRef.current = {
                                value: newValue,
                                initialized: true
                            };
                            listen();
                        }
                    });
                },
                getSnapshot: ()=>{
                    const newValue = $val.get();
                    // Return cached value if content is equal (preserves reference stability)
                    if (cachedValueRef.current.initialized && isEqual(cachedValueRef.current.value, newValue)) {
                        return cachedValueRef.current.value;
                    }
                    // Update cache and return new value
                    cachedValueRef.current = {
                        value: newValue,
                        initialized: true
                    };
                    return newValue;
                }
            };
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            $val,
            isEqual
        ]);
        return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    } finally{
        isInRender.current = false;
    }
}
